import { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext.jsx';
import { fonts as fontsApi } from '../api/client.js';

export default function HealthDashboard({ onClose, onSelectFont }) {
  const { theme } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);

  const fetchHealth = () => {
    setLoading(true);
    fontsApi.health()
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchHealth(); }, []);

  const handleScanAll = async () => {
    setScanning(true);
    try {
      await fontsApi.healthScan();
      fetchHealth();
    } catch (err) {
      console.error('Scan failed:', err);
    } finally {
      setScanning(false);
    }
  };

  const summary = data?.summary || { healthy: 0, warning: 0, error: 0, unchecked: 0 };
  const total = summary.healthy + summary.warning + summary.error + summary.unchecked;

  const cards = [
    { label: 'Healthy', count: summary.healthy, color: theme.green },
    { label: 'Warning', count: summary.warning, color: theme.orange },
    { label: 'Error', count: summary.error, color: theme.red },
    { label: 'Unchecked', count: summary.unchecked, color: theme.textFaint },
  ];

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      background: theme.overlay,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '90%', maxWidth: 800, maxHeight: '85vh',
          background: theme.bgElevated, borderRadius: 0,
          border: `2px solid ${theme.text}`, overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: `2px solid ${theme.text}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <h2 style={{ fontFamily: "'Aeonik', Arial, Helvetica, sans-serif", fontWeight: 450, margin: 0, fontSize: 22, color: theme.text }}>font health</h2>
            <div style={{ fontSize: 12, color: theme.textFaint, marginTop: 4 }}>
              {total} fonts in library
            </div>
          </div>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <span
              onClick={scanning ? undefined : handleScanAll}
              style={{
                fontSize: 13, textTransform: 'lowercase',
                color: scanning ? theme.textFaint : theme.accent,
                cursor: scanning ? 'default' : 'pointer',
              }}
            >{scanning ? 'scanning\u2026' : 'scan all'}</span>
            <span onClick={onClose} style={{ fontSize: 13, color: theme.textFaint, cursor: 'pointer', lineHeight: 1 }}>{'\u2715'}</span>
          </div>
        </div>

        {/* Summary Cards */}
        <div style={{
          padding: '16px 24px', borderBottom: `1px solid ${theme.border}`,
          display: 'flex', gap: 1, background: theme.border,
        }}>
          {cards.map(c => (
            <div key={c.label} style={{
              flex: 1, padding: '14px 16px', background: theme.bgElevated, textAlign: 'center',
            }}>
              <div style={{ fontSize: 24, fontWeight: 700, color: c.color }}>{c.count}</div>
              <div style={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: theme.textFaint, marginTop: 4 }}>{c.label}</div>
            </div>
          ))}
        </div>

        {/* Font Issues List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: 40, color: theme.textMuted, fontSize: 13 }}>
              loading health data\u2026
            </div>
          )}

          {!loading && (!data?.fonts?.length) && (
            <div style={{ textAlign: 'center', padding: 40, color: theme.textFaint }}>
              <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.5 }}>{'\u2713'}</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>all fonts are healthy</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>no issues detected in your font library</div>
            </div>
          )}

          {data?.fonts?.map(font => {
            const issues = typeof font.health_issues === 'string'
              ? JSON.parse(font.health_issues)
              : (font.health_issues || []);
            const isError = font.health_status === 'error';
            const statusColor = isError ? theme.red : theme.orange;

            return (
              <div key={font.id} style={{
                marginBottom: 12, padding: 14,
                background: 'transparent', borderRadius: 0,
                border: `1px solid ${statusColor}`,
                cursor: 'pointer',
              }}
                onClick={() => { onSelectFont?.(font.id); onClose(); }}
              >
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  marginBottom: 8,
                }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: theme.text }}>
                    {font.name}
                    <span style={{ fontSize: 11, color: theme.textMuted, fontWeight: 400, marginLeft: 8 }}>
                      {font.family}
                    </span>
                  </div>
                  <span style={{
                    fontSize: 10, fontWeight: 700,
                    color: statusColor,
                    border: `1px solid ${statusColor}`,
                    padding: '3px 8px', borderRadius: 0,
                    textTransform: 'uppercase',
                  }}>{font.health_status}</span>
                </div>
                {issues.map((issue, i) => (
                  <div key={i} style={{
                    fontSize: 11, color: theme.textMuted, padding: '2px 0',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                    <span style={{ color: statusColor }}>
                      {issue.type === 'warning' ? '\u26A0' : '\u2718'}
                    </span>
                    {issue.message}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
